import { useEffect, useState } from "react";
import { Plus, Trash2, Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiError } from "../lib/api";

/** Admin page to post announcements that show up on /whats-new. */
export default function AdminAnnouncements() {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [bodyAr, setBodyAr] = useState("");
    const [bodyEn, setBodyEn] = useState("");
    const [kind, setKind] = useState("update");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try { const r = await api.get("/announcements"); setItems(r.data.items || []); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!titleAr.trim() || !bodyAr.trim()) { toast.error("العنوان والنص بالعربية مطلوبان"); return; }
        setSaving(true);
        try {
            await api.post("/admin/announcements", { title_ar: titleAr, title_en: titleEn, body_ar: bodyAr, body_en: bodyEn, kind });
            toast.success("تم النشر");
            setTitleAr(""); setTitleEn(""); setBodyAr(""); setBodyEn(""); setKind("update");
            setShowForm(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("حذف هذا الإعلان؟")) return;
        try { await api.delete(`/admin/announcements/${id}`); await load(); toast.success("حُذف"); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="min-h-screen bg-neutral-50 p-4" dir="rtl">
            <div className="mx-auto max-w-4xl space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="font-heading text-2xl font-bold text-neutral-900">إدارة "ما الجديد"</h1>
                    <Button onClick={() => setShowForm(!showForm)} className="gap-1 bg-amber-700 text-white hover:bg-amber-800" data-testid="admin-ann-new-btn">
                        <Plus className="h-4 w-4" /> إعلان جديد
                    </Button>
                </div>

                {showForm ? (
                    <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-200">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <Label className="text-sm">النوع</Label>
                                <Select value={kind} onValueChange={setKind}>
                                    <SelectTrigger data-testid="admin-ann-kind"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="feature">ميزة جديدة</SelectItem>
                                        <SelectItem value="update">تحديث</SelectItem>
                                        <SelectItem value="announcement">إعلان</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="hidden sm:block" />
                            <div>
                                <Label className="text-sm">العنوان (عربي) *</Label>
                                <Input value={titleAr} onChange={(e) => setTitleAr(e.target.value)} data-testid="admin-ann-title-ar" />
                            </div>
                            <div>
                                <Label className="text-sm">Title (English)</Label>
                                <Input dir="ltr" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} data-testid="admin-ann-title-en" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-sm">النص (عربي) *</Label>
                                <Textarea rows={4} value={bodyAr} onChange={(e) => setBodyAr(e.target.value)} data-testid="admin-ann-body-ar" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-sm">Body (English)</Label>
                                <Textarea rows={4} dir="ltr" value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} data-testid="admin-ann-body-en" />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowForm(false)}>إلغاء</Button>
                            <Button onClick={create} disabled={saving} className="gap-2 bg-amber-700 text-white hover:bg-amber-800" data-testid="admin-ann-save-btn">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                نشر
                            </Button>
                        </div>
                    </div>
                ) : null}

                <div className="space-y-3">
                    {items.length === 0 ? (
                        <div className="rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-200">
                            <Megaphone className="mx-auto h-8 w-8 text-neutral-300" />
                            <p className="mt-3 text-sm text-neutral-500">لا توجد إعلانات بعد</p>
                        </div>
                    ) : (
                        items.map((a) => (
                            <div key={a.id} className="flex items-start justify-between gap-3 rounded-2xl bg-white p-4 ring-1 ring-neutral-200">
                                <div className="min-w-0 flex-1">
                                    <div className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">{a.kind}</div>
                                    <h3 className="font-heading text-base font-bold">{a.title_ar}</h3>
                                    {a.title_en ? <div className="text-xs text-neutral-500" dir="ltr">{a.title_en}</div> : null}
                                    <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-700">{a.body_ar}</p>
                                    <div className="mt-1 text-[10px] text-neutral-400">{new Date(a.created_at).toLocaleString("ar-SA")}</div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => remove(a.id)} className="text-red-700 hover:bg-red-50" data-testid={`admin-ann-del-${a.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
